'use client'

import { Button } from '@vinted/web-ui'
import { forwardRef, useContext, useEffect, useImperativeHandle, useState } from 'react'

import { useFetch } from '@marketplace-web/shared/api-client'
import { useTracking } from '@marketplace-web/shared/event-tracker'
import { UiState } from '@marketplace-web/shared/ui-state'
import {
  ConversationActionButton,
  ConversationActionProps,
} from '@marketplace-web/domain/conversation-action'
import { ReturnShippingModal } from '@marketplace-web/domain/conversation-shipping-data'

import { clickEvent } from '_libs/common/event-tracker/events'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'
import { getSnadComplaint } from 'data/api/get-snad-complaint'
import { prepareReturnLabelOrder } from 'data/api/prepare-return-label-order'
import { transformSnadComplaintDto } from 'data/transformers/complaint'
import { transformReturnShipmentOrderDto } from 'data/transformers/return-shipment-order'
import { ComplaintModel } from 'types/models/complaint'

import GenericErrorModal from '../GenericErrorModal'
import RequestReturnModal from '../RequestReturnModal'
import ReturnShipmentCheckout from '../ReturnShipmentCheckout'
import { RequestReturnDetailsContext } from './RequestReturnProvider'

export interface RequestReturnRef {
  handleReturnModalOpen: () => void
}

type Props = Partial<ConversationActionProps> & {
  isChooseResolutionFlow?: boolean
  complaint?: ComplaintModel | null
  url?: string
  text?: string
  conversationTransactionId?: number
  conversationTransactionIsBundle?: boolean
}

const RequestReturn = forwardRef(
  (
    {
      primary,
      title,
      inverse,
      theme,
      size,
      complaint: preFetchedComplaint,
      url,
      text,
      isChooseResolutionFlow,
      conversationTransactionId,
      conversationTransactionIsBundle,
    }: Props,
    ref,
  ) => {
    const { track } = useTracking()

    const [activeDialog, setActiveDialog] = useState<ReturnShippingModal | null>(
      ReturnShippingModal.None,
    )

    const buttonTitle = title || text

    const {
      fetch: fetchSnadComplaint,
      transformedData: fetchedComplaint,
      isLoading: isComplaintLoading,
    } = useFetch(getSnadComplaint, transformSnadComplaintDto)

    const { fetch: fetchPrepareReturnLabelOrder, transformedData: returnShipmentOrder } = useFetch(
      prepareReturnLabelOrder,
      response => transformReturnShipmentOrderDto(response.return_label_order),
    )

    const complaint = preFetchedComplaint || fetchedComplaint
    const transactionId = conversationTransactionId || complaint?.transactionId

    const {
      requestReturnDetails: returnShipment,
      fetchRequestReturnDetails,
      updateSelectedPaymentSide,
      uiState,
      requestReturnDetailsError,
      setRequestReturnDetailsError,
    } = useContext(RequestReturnDetailsContext)

    const openErrorDialog = () => setActiveDialog(ReturnShippingModal.GenericErrorModal)

    useEffect(() => {
      if (!requestReturnDetailsError) return

      openErrorDialog()
    })

    const fetchReturnShipment = () => {
      if (!transactionId) return

      fetchRequestReturnDetails({ transactionId })
    }

    const fetchComplaint = async () => {
      if (preFetchedComplaint || !transactionId) return

      const response = await fetchSnadComplaint(transactionId)

      if (response.error) {
        openErrorDialog()
      }
    }

    const trackRequestReturnModalOpen = () => {
      if (!transactionId) return

      track(
        clickEvent({
          screen: Screen.ComplaintProof,
          target: ClickableElement.ConfirmSubmitRequestReturn,
          targetDetails: JSON.stringify({
            transaction_id: transactionId,
          }),
        }),
      )
    }

    const handleReturnModalOpen = () => {
      fetchComplaint()
      fetchReturnShipment()
      trackRequestReturnModalOpen()
      setActiveDialog(ReturnShippingModal.RequestReturnModal)
    }

    useImperativeHandle(ref, () => ({
      handleReturnModalOpen,
    }))

    const handleReturnModalClose = () => {
      setActiveDialog(ReturnShippingModal.None)
      updateSelectedPaymentSide(null)
      setRequestReturnDetailsError(undefined)
    }

    const prepareReturnLabelOrderForCheckout = () => {
      if (!transactionId) return
      if (!returnShipment) return
      if (!returnShipment.pickupPointDetails) return
      if (!returnShipment.userAddress) return
      if (!returnShipment.userAddress.id) return
      if (!returnShipment.packageTypeId) return

      fetchPrepareReturnLabelOrder({
        transactionId: transactionId.toString(),
        pickupPointCode: returnShipment.pickupPointDetails?.pickupPointCode,
        packageTypeId: returnShipment.packageTypeId,
        addressId: returnShipment.userAddress?.id.toString(),
      })

      setActiveDialog(ReturnShippingModal.Checkout)
    }

    const handleProceedToCheckout = () => {
      fetchReturnShipment()
      if (transactionId) fetchRequestReturnDetails({ transactionId })
      prepareReturnLabelOrderForCheckout()
    }

    const renderActiveModal = () => {
      switch (activeDialog) {
        case ReturnShippingModal.RequestReturnModal:
          return (
            <RequestReturnModal
              transactionId={transactionId}
              url={url}
              onClose={handleReturnModalClose}
              onProceedToCheckout={handleProceedToCheckout}
              isDataLoading={uiState === UiState.Pending || isComplaintLoading}
              onFetchReturnShipment={fetchReturnShipment}
              complaint={complaint}
              conversationTransactionIsBundle={conversationTransactionIsBundle}
            />
          )
        case ReturnShippingModal.Checkout:
          return (
            <ReturnShipmentCheckout
              returnShipmentOrder={returnShipmentOrder}
              onErrorDialogOpen={openErrorDialog}
            />
          )
        case ReturnShippingModal.GenericErrorModal:
          return <GenericErrorModal isShown onClose={handleReturnModalClose} />
        default:
          return null
      }
    }

    if (!transactionId) return null

    return (
      <>
        {!isChooseResolutionFlow && (
          <ConversationActionButton
            text={buttonTitle}
            primary={primary}
            size={size || Button.Size.Medium}
            theme={theme}
            inverse={inverse}
            onClick={handleReturnModalOpen}
            testId="request-return-action-button"
          />
        )}
        {renderActiveModal()}
      </>
    )
  },
)

export default RequestReturn
